import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  deleteExhibitorProduct,
  fetchExhibitorProducts
} from '../../../Reducer/exhibitorProductSlice';
import ExhibitorProductTable from './ExhibitorProductTable';
import ProductFilter from './productFilter/ProductFilter';
import LoadingSpinner from './LoadingSpinner.jsx';
import {
  Button,
  Typography,
  Box,
  Pagination,
  Grid,
  TextField
} from '@mui/material';
import { toast } from 'react-toastify';
import DownloadProductCsv from './ex_product_component/DownloadProductCsv.jsx';
import CsvUploader from './ex_product_component/CsvUploader.jsx';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const TitleLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: 'black',
}));

const AddProductButton = styled(Button)(({ theme }) => ({
  fontWeight: 100,
  textTransform: 'capitalize',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "#757575",
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ExhibitorProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { exhibitorProducts, totalPages, loading, error } = useSelector((state) => state.exhibitorProducts);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    event_name: '',
    location: '',
    total_exhibitor: '',
    price_min: '',
    price_max: '',
    event_date_from: '',
    event_date_to: '',
    industry: '',
    product_type: '',
  });

  useEffect(() => {
    dispatch(
      fetchExhibitorProducts({
        page: currentPage,
        limit: productsPerPage,
        filters: { ...filters, event_name: searchTerm },
      })
    );
  }, [dispatch, currentPage, filters, searchTerm, productsPerPage]);

  const onDeleteProduct = (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this product?");
    if (confirmDelete) {
      dispatch(deleteExhibitorProduct(id))
        .then(() => toast.success('Product deleted successfully'))
        .catch(() => toast.error('Failed to delete product'));
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // ✅ Handle Date Change for From / To filters
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
      <TitleContainer>
        <TitleLeft>
          <ShoppingCartIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'black' }} />
          <TitleText variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Exhibitor Products
          </TitleText>
        </TitleLeft>

        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/dashboard/add-product')}
          startIcon={<AddIcon />}
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            padding: { xs: '6px 12px', sm: '8px 16px' },
          }}
        >
          Add New Product
        </Button>
      </TitleContainer>

      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        {/* 🔽 Existing Filter Component */}
        <ProductFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
        />

        {/* 🔽 Added Date Filter Section */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date From"
              type="date"
              name="event_date_from"
              value={filters.event_date_from}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date To"
              type="date"
              name="event_date_to"
              value={filters.event_date_to}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        {loading && (
          <Box textAlign="center" mt={2}>
            <LoadingSpinner />
          </Box>
        )}

        {error && (
          <Box textAlign="center" color="error.main" mt={2}>
            <Typography>Error: {error}</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                dispatch(
                  fetchExhibitorProducts({
                    page: currentPage,
                    limit: productsPerPage,
                    filters,
                  })
                )
              }
            >
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ mt: 3 }}>
              <ExhibitorProductTable
                products={exhibitorProducts}
                onEdit={(product) =>
                  navigate(`/dashboard/edit-product/${product.ex_product_id}`, {
                    state: { product },
                  })
                }
                onAddKeyExhibitor={(product) =>
                  navigate(`/dashboard/addkeyexhibitor/${product.woocommerce_product_id}`)
                }
                onAddSeo={(product) =>
                  navigate(`/dashboard/addseo/${product.ex_product_id}`)
                }
                onDeleteProduct={onDeleteProduct}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              sx={{
                mt: { xs: 2, sm: 3 },
                mb: { xs: 35, sm: 2 },
                overflowX: 'auto',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    minWidth: { xs: 30, sm: 36 },
                    height: { xs: 30, sm: 36 },
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ExhibitorProductList;
